/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { FormattedDate, FormattedMessage, type MessageDescriptor } from 'react-intl-next';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SearchResultProps } from '../../types';
import { ProfileAvatar } from '../profile-avatar';

import { messages } from './messages';

export const JIRA_DC_INTEGRATION_IDS: string[] = [
	'ari:cloud:platform::integration/jira-dc',
	'ari:cloud:platform::integration/jira-dc-secondary',
	'ari:cloud:platform::integration/jira-dc-tertiary',
];

export const isSearchJiraDcProps: (
	props: SearchResultProps,
) => props is SearchResultProps<unknown> = (
	props: SearchResultProps,
): props is SearchResultProps<unknown> => {
	return (
		props?.node?.__typename === 'SearchResultGraphDocument' &&
		JIRA_DC_INTEGRATION_IDS.includes(props?.node?.integrationId ?? '')
	);
};

export const formatKey: (key: string | null | undefined) => string = (
	key: string | null | undefined,
): string => (key ? key + ' ' : '');

export const getAssigneeOrCreator: (
	formatMessage: (descriptor: MessageDescriptor, parameters?: any) => string,
	assigneeName?: string | null,
	createdByName?: string | null,
) => JSX.Element | null = (
	formatMessage: (descriptor: MessageDescriptor, parameters?: any) => string,
	assigneeName?: string | null,
	createdByName?: string | null,
) => {
	let actionLabel: string;
	const fullName = assigneeName || createdByName || '';
	if (!fullName) {
		return null;
	}
	const firstName = fullName?.split(' ')?.[0];
	if (assigneeName) {
		actionLabel = formatMessage(messages.isAssigned, {
			firstName,
			isAssigned: firstName ? 'internal' : 'external',
		});
	} else {
		actionLabel = formatMessage(messages.created, {
			firstName,
			created: firstName ? 'internal' : 'external',
		});
	}

	return (
		<Inline space="space.025" alignBlock="center">
			<ProfileAvatar name={fullName} />
			<Box xcss={cx(styles.nameContainer)}>{actionLabel}</Box>
		</Inline>
	);
};

export const getStatusAndDate: (
	status?: string | null,
	dueDate?: string | null,
) => JSX.Element | null = (status?: string | null, dueDate?: string | null) => {
	if (!status) {
		return null;
	}
	const statusLabel = status.replace(/[-_]+/g, ' ');

	const statusLozenge = (
		<Lozenge appearance={getStatusLozengeAppearance(statusLabel)}>{statusLabel}</Lozenge>
	);
	const dateLozenge = (
		<Lozenge>
			<FormattedDate value={dueDate ?? undefined} month="short" day="numeric" />
		</Lozenge>
	);

	return (
		<Box xcss={cx(styles.statusAndDateContainerStyles)}>
			{dueDate ? (
				<FormattedMessage {...messages.for} values={{ statusLozenge, dateLozenge }} />
			) : (
				statusLozenge
			)}
		</Box>
	);
};

const getStatusLozengeAppearance = (label: string) => {
	label = label.replace(/[\s]+/g, '').toLowerCase();
	switch (true) {
		case ['onhold'].includes(label):
			return 'default'; // gray

		case ['solved', 'complete'].includes(label):
			return 'success'; // green

		case ['open', 'offtrack'].includes(label):
			return 'removed'; // red

		case ['new', 'atrisk'].includes(label):
			return 'moved'; // yellow

		case ['pending', 'ontrack'].includes(label):
			return 'inprogress'; // blue
	}
	return 'inprogress'; // blue for custom status labels
};

const styles = cssMap({
	nameContainer: {
		whiteSpace: 'nowrap',
	},
	statusAndDateContainerStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});
